/**
 * Rate Limit Utility Functions
 *
 * Provides utility functions for endpoint normalization and display name generation
 * to support dynamic endpoint-specific rate limiting
 */

/**
 * Normalizes an endpoint to match backend's endpoint normalization
 * @param {string} method - HTTP method (GET, POST, etc.)
 * @param {string} path - Request path/URL
 * @returns {string} Normalized endpoint key
 */
export function normalizeEndpoint(method, path) {
	let endpoint = path;

	// Remove base URL if present (consistent with existing $http usage)
	if (endpoint.startsWith("http")) {
		try {
			const url = new URL(endpoint);
			endpoint = url.pathname;
		} catch (e) {
			// If URL parsing fails, continue with the original endpoint
		}
	}

	// Remove query parameters
	const questionMarkIndex = endpoint.indexOf("?");
	if (questionMarkIndex >= 0) {
		endpoint = endpoint.substring(0, questionMarkIndex);
	}

	// Convert to lowercase for consistency
	endpoint = endpoint.toLowerCase();

	// Remove trailing slashes
	endpoint = endpoint.replace(/\/+$/, "");

	// Replace any characters that might cause issues in cache keys
	endpoint = endpoint.replace(/[: ]/g, "_");

	// Combine with method (lowercase)
	return `${method.toLowerCase()}_${endpoint}`;
}

/**
 * Gets a human-readable display name for an endpoint
 * @param {string} endpoint - Normalized endpoint key
 * @param {Object} displayNames - Map of endpoint to display names
 * @returns {string} Display name for the endpoint
 */
export function getEndpointDisplayName(endpoint, displayNames = {}) {
	// Check for predefined display name
	if (displayNames[endpoint]) {
		return displayNames[endpoint];
	}

	// Generate display name from endpoint
	// Remove method prefix
	let displayName = endpoint.replace(/^(get|post|put|delete|patch)_/, "");

	// Convert path to readable format
	displayName = displayName
		.replace(/^\/api\//, "") // Remove /api/ prefix
		.replace(/_/g, " ") // Replace underscores with spaces
		.replace(/\//g, " - ") // Replace slashes with dashes
		.split(" ")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize words
		.join(" ");

	return displayName;
}

/**
 * Extracts the feature name from an endpoint for prioritization
 * @param {string} endpoint - Normalized endpoint key
 * @returns {string} Feature name (e.g., 'search', 'suggest', 'auth')
 */
export function getEndpointFeature(endpoint) {
	// Extract the main feature from the endpoint
	const match = endpoint.match(/^[^_]+_\/api\/([^/]+)/);
	if (match) {
		return match[1];
	}

	// Fallback to extracting from the endpoint path
	const pathMatch = endpoint.match(/^[^_]+_([^_/]+)/);
	if (pathMatch) {
		return pathMatch[1];
	}

	return "other";
}

/**
 * Gets the priority of an endpoint for notification ordering
 * Higher priority endpoints should be shown first when multiple rate limits occur
 * @param {string} endpoint - Normalized endpoint key
 * @returns {number} Priority value (lower = higher priority)
 */
export function getEndpointPriority(endpoint) {
	const feature = getEndpointFeature(endpoint);

	const priorityMap = {
		search: 1, // Search operations are highest priority
		suggest: 2, // Autocomplete/suggestions
		mail: 3, // Mail operations
		account: 3, // Account operations
		auth: 4, // Authentication
		faq: 5, // FAQ/Help content
		vendor: 5, // Vendor operations
		other: 10, // Everything else
	};

	return priorityMap[feature] || priorityMap.other;
}

/**
 * Formats remaining time in a human-readable format
 * @param {number} seconds - Remaining seconds
 * @returns {string} Formatted time string
 */
export function formatRemainingTime(seconds) {
	if (!seconds || seconds <= 0) return "0s";

	const minutes = Math.floor(seconds / 60);
	const remainingSeconds = seconds % 60;

	if (minutes > 0) {
		return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
	}

	return `${remainingSeconds}s`;
}

/**
 * Predefined display names for common endpoints
 */
export const ENDPOINT_DISPLAY_NAMES = {
	"post_/api/assistant/conversations": "Assistant",
	"get_/api/assistant/usage": "Assistant Usage",
	"post_/api/search/product": "Product Search",
	"post_/api/search/formulation": "Formulation Search",
	"post_/api/search/glossary-term": "Glossary Search",
	"get_/api/suggest/product": "Product Suggestions",
	"get_/api/suggest/product-class": "Product Class Suggestions",
	"get_/api/suggest/cas-number": "CAS Number Suggestions",
	"get_/api/suggest/un-number": "UN Number Suggestions",
	"get_/api/suggest/vendor": "Vendor Suggestions",
	"get_/api/suggest/product-use": "Product Use Suggestions",
	"get_/api/suggest/inci": "INCI Suggestions",
	"get_/api/suggest/formulation": "Formulation Suggestions",
	"get_/api/suggest/formulation-vendor": "Formulation Vendor Suggestions",
	"get_/api/suggest/formulation-use": "Formulation Use Suggestions",
	"get_/api/suggest/glossary-term": "Glossary Term Suggestions",
	"post_/api/auth/login": "Login",
	"get_/api/auth/session": "Session Verification",
	"post_/api/mail/list": "Mail List",
	"post_/api/mail/detail": "Mail Details",
	"get_/api/faq/list-faqs": "FAQ List",
	"post_/api/account/update": "Account Update",
	"post_/api/search/log": "Search Logging",
	"post_/api/search/log-activity": "Activity Logging",
};

/**
 * Checks if an endpoint should be tracked for rate limiting
 * Some endpoints like static resources shouldn't be tracked
 * @param {string} endpoint - Normalized endpoint key
 * @returns {boolean} Whether to track this endpoint
 */
export function shouldTrackEndpoint(endpoint) {
	// Don't track certain endpoints
	const excludePatterns = [
		// File storage and static content
		/_\/api\/storage\//, // File storage operations (preview, download, tokens)

		// Static reference data
		/_\/api\/vendor\/list-vendors/, // Vendor list (loaded once, cached)
		/_\/api\/suggest\/countries/, // Country list (static)
		/_\/api\/suggest\/states/, // State list (static)
		/_\/api\/faq\/list-faqs/, // FAQ list (relatively static)
		/_\/api\/cross-reference\/get/, // Cross-reference data (read-only reference)

		// System and configuration
		/_\/api\/system\//, // System configuration and health

		// Authentication and session management
		/_\/api\/auth\/session/, // Session verification - critical for app functionality
		/_\/api\/auth\/validate-iframe-origin/, // Iframe validation
		/_\/api\/auth\/vendor-logo/, // Vendor logo retrieval
		/_\/api\/auth\/logout/, // Logout should always work

		// Logging and auditing (should never be blocked)
		/_\/api\/search\/log$/, // Search logging
		/_\/api\/search\/log-activity/, // Activity logging
		/_\/api\/.*audit/, // Any audit endpoints
		/_\/api\/.*log/, // Any logging endpoints
	];

	return !excludePatterns.some((pattern) => pattern.test(endpoint));
}
